import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Calendar,
  AlertCircle,
  CheckCircle,
  Ticket,
  Inbox,
  Plus,
  DollarSign,
  Boxes,
} from 'lucide-react';
import { ScorecardBookingCard, FreeSlotChip, StatusPill, EmptyState } from '../../components/ui';
import { getBookingCalendarDateStr, getLiveScheduleStatus, formatInr } from './homeDashboardUtils';

// Map a booking's amount/advance to a StatusPill payment key.
function paymentKeyFor(booking) {
  const total = parseFloat(booking.totalAmount) || parseFloat(booking.price) || 0;
  const advance = parseFloat(booking.advanceAmount) || 0;
  if (total > 0 && total - advance <= 0) return 'paid';
  if (advance > 0) return 'partial';
  return 'pending';
}

export default function HomeDashboardContent(props) {
  const {
    todaysBookings,
    dayName,
    dayNumber,
    slotSummary,
    today,
    totalSlotsToday,
    paymentsSummary,
    overdue,
    upcomingBookings,
    upcomingFingerprintCounts,
    dynamicInsights,
    pendingRequestCount,
    operationsSummary,
    openTicketsCount,
    inventoryLowStockCount,
    inventoryTotalItems,
    summaryLoading,
    lastSyncedAt,
    moduleGrid,
  } = props;

  const navigate = useNavigate();
  const goBookNow = () => navigate('/dashboard/book-now');

  // Derive the headline "Filled %" from the same per-service base as the rows
  // below so the card stays internally consistent.
  const slotTotals = Object.values(slotSummary).reduce(
    (acc, d) => ({ booked: acc.booked + d.booked, total: acc.total + d.total }),
    { booked: 0, total: 0 }
  );
  const filledPercent = slotTotals.total
    ? Math.min(100, Math.round((slotTotals.booked / slotTotals.total) * 100))
    : 0;

  const freeSlotsToday = Math.max(0, (totalSlotsToday || 0) - todaysBookings.length);

  return (
    <>
      {/* Today's schedule | Alerts + Slot fill */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Today's schedule */}
        <div className="lg:col-span-2 ga-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-bold text-ga-text-primary">Today&apos;s schedule</h2>
            <span className="text-sm text-ga-text-muted">{dayName} {dayNumber}</span>
          </div>

          {todaysBookings.length > 0 ? (
            <div className="space-y-3">
              {todaysBookings.map((booking, index) => {
                const serviceName = booking.serviceName || 'Unknown Service';
                const bookedBy = booking.teamA && booking.teamB
                  ? 'Fully booked'
                  : (booking.bookedBy || booking.teamA?.captainName || 'Guest');
                const amount = formatInr(booking.totalAmount || booking.price || 0);
                const liveStatus = getLiveScheduleStatus(booking);

                return (
                  <ScorecardBookingCard
                    key={booking.id || index}
                    title={bookedBy}
                    service={serviceName}
                    ground={booking.groundName}
                    time={booking.timeSlot || '00:00-02:00'}
                    status={liveStatus.status}
                    statusLabel={liveStatus.label}
                    amount={amount}
                    paymentStatus={paymentKeyFor(booking)}
                  />
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={Calendar}
              title="No bookings for today"
              description="Slots are open — start filling today's schedule."
              action={
                <FreeSlotChip onClick={goBookNow}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Book a slot
                </FreeSlotChip>
              }
            />
          )}

          {/* Free-slot affordance */}
          {todaysBookings.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ga-border-subtle pt-4">
              <span className="text-sm text-ga-text-muted">
                {freeSlotsToday > 0 ? `${freeSlotsToday} slots still open today` : 'Add another booking'}
              </span>
              <FreeSlotChip onClick={goBookNow} className="ml-auto">
                <Plus className="mr-1 h-3.5 w-3.5" /> Book
              </FreeSlotChip>
            </div>
          )}
        </div>

        {/* Alerts + Slot fill */}
        <div className="space-y-4 md:space-y-6">
          {/* Alerts & Tasks */}
          <div className="ga-card">
            <h2 className="text-lg font-bold text-ga-text-primary mb-4">Alerts &amp; tasks</h2>
            <div className="space-y-3">
              {overdue.length > 0 && (
                <Link to="/dashboard/operations" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-danger/10 border border-ga-danger/30">
                  <AlertCircle className="h-5 w-5 text-ga-danger mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">{overdue.length} equipment overdue</p>
                    <p className="text-xs text-ga-text-muted mt-1">View equipment →</p>
                  </div>
                </Link>
              )}
              {pendingRequestCount > 0 && (
                <Link to="/dashboard/booking-requests" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-warning/10 border border-ga-warning/30">
                  <Inbox className="h-5 w-5 text-ga-warning mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">
                      {pendingRequestCount} booking request{pendingRequestCount === 1 ? '' : 's'} awaiting confirmation
                    </p>
                    <p className="text-xs text-ga-text-muted mt-1">Review &amp; approve →</p>
                  </div>
                </Link>
              )}
              {(paymentsSummary.pendingCount ?? 0) > 0 && (
                <Link to="/dashboard/payments" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-warning/10 border border-ga-warning/30">
                  <AlertCircle className="h-5 w-5 text-ga-warning mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">
                      {paymentsSummary.pendingCount} payment{paymentsSummary.pendingCount === 1 ? '' : 's'} pending
                      {(paymentsSummary.pendingAmount ?? 0) > 0 && (
                        <span className="text-ga-warning"> · ₹{(paymentsSummary.pendingAmount ?? 0).toLocaleString('en-IN')} due</span>
                      )}
                    </p>
                    <p className="text-xs text-ga-text-muted mt-1">View payments →</p>
                  </div>
                </Link>
              )}
              {(operationsSummary.totalTasks ?? 0) > 0 && (
                <Link to="/dashboard/operations/daily" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-success/10 border border-ga-success/30">
                  <CheckCircle className="h-5 w-5 text-ga-success mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">Daily ops {operationsSummary.completedCount}/{operationsSummary.totalTasks} done</p>
                    <p className="text-xs text-ga-text-muted mt-1">Complete remaining →</p>
                  </div>
                </Link>
              )}
              {inventoryLowStockCount > 0 && (
                <Link to="/dashboard/assets/inventory?low_stock=true" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-danger/10 border border-ga-danger/30">
                  <Boxes className="h-5 w-5 text-ga-danger mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">
                      {inventoryLowStockCount} inventory item{inventoryLowStockCount === 1 ? '' : 's'} low on stock
                    </p>
                    <p className="text-xs text-ga-text-muted mt-1">View inventory →</p>
                  </div>
                </Link>
              )}
              {inventoryTotalItems > 0 && inventoryLowStockCount === 0 && (
                <Link to="/dashboard/assets/inventory" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-primary/10 border border-ga-primary/30">
                  <Boxes className="h-5 w-5 text-ga-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">
                      {inventoryTotalItems} inventory item{inventoryTotalItems === 1 ? '' : 's'} tracked
                    </p>
                    <p className="text-xs text-ga-text-muted mt-1">View inventory →</p>
                  </div>
                </Link>
              )}
              {openTicketsCount > 0 && (
                <Link to="/dashboard/help-center" className="flex items-start gap-3 p-3 rounded-ga-sm bg-ga-info/10 border border-ga-info/30">
                  <Ticket className="h-5 w-5 text-ga-info mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ga-text-primary">{openTicketsCount} support ticket(s) open</p>
                    <p className="text-xs text-ga-text-muted mt-1">View tickets →</p>
                  </div>
                </Link>
              )}
              {overdue.length === 0 &&
                pendingRequestCount === 0 &&
                (paymentsSummary.pendingCount ?? 0) === 0 &&
                (operationsSummary.totalTasks ?? 0) === 0 &&
                inventoryLowStockCount === 0 &&
                inventoryTotalItems === 0 &&
                openTicketsCount === 0 && (
                  <p className="text-sm text-ga-text-muted px-1 py-2">No alerts right now.</p>
                )}
            </div>
          </div>

          {/* Slot fill */}
          <div className="ga-card">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h2 className="text-lg font-bold text-ga-text-primary">Slot fill by service</h2>
              {Object.keys(slotSummary).length > 0 && (
                <span className="text-sm text-ga-text-muted shrink-0">Filled {filledPercent}%</span>
              )}
            </div>
            <div className="space-y-4">
              {Object.entries(slotSummary).length > 0 ? (
                Object.entries(slotSummary).map(([serviceName, data]) => {
                  const percentage = Math.round((data.booked / data.total) * 100);
                  return (
                    <div key={serviceName}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-ga-text-muted">{serviceName}</span>
                        <span className="text-xs text-ga-text-subtle">{data.booked}/{data.total}</span>
                      </div>
                      <div className="w-full bg-ga-bg-page rounded-full h-2">
                        <div
                          className="bg-ga-primary h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(100, percentage)}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-ga-md border border-ga-border-subtle bg-ga-bg-page/50 px-4 py-6 text-center text-sm text-ga-text-muted">
                  No slots filled today ({format(today, 'MMM d')}).
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Module grid */}
      {moduleGrid}

      {/* Upcoming bookings */}
      <div className="ga-card">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h2 className="text-lg md:text-xl font-bold text-ga-text-primary">Upcoming bookings</h2>
          <span className="text-sm text-ga-text-muted shrink-0">Next 8</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ga-border-subtle">
            <thead className="bg-ga-bg-page">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ga-border-subtle">
              {upcomingBookings.length > 0 ? (
                upcomingBookings.map((booking, index) => {
                  const customerName = booking.bookedBy || booking.teamA?.captainName || booking.contactName || 'Guest';
                  const cal = getBookingCalendarDateStr(booking);
                  const bookingDate = format(parseISO(`${cal}T12:00:00`), 'MMM d');
                  const fp = [
                    cal,
                    booking.timeSlot || '',
                    booking.serviceName || '',
                    customerName,
                  ].join('\u0001');
                  const sameSlotGroup = (upcomingFingerprintCounts[fp] || 0) > 1;

                  return (
                    <tr key={booking.id != null ? `b-${booking.id}-${index}` : `idx-${index}`} className="hover:bg-ga-primary-soft/10">
                      <td
                        className="px-4 py-3 whitespace-nowrap text-sm text-ga-text-primary align-top"
                        title={
                          sameSlotGroup
                            ? 'Multiple rows share this date, time, service, and customer—common for team matches (both sides) or related reservations.'
                            : undefined
                        }
                      >
                        <span>#{booking.id != null ? booking.id : index + 1}</span>
                        {sameSlotGroup && (
                          <span className="block text-xs font-normal text-ga-text-subtle mt-0.5">Shared slot group</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-ga-text-muted">
                        {bookingDate} {booking.timeSlot || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-ga-text-primary">
                        {booking.serviceName || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-ga-text-muted">{customerName}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-ga-text-primary">
                        ₹{parseFloat(booking.totalAmount || booking.price || 0).toFixed(0)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusPill status={paymentKeyFor(booking)} size="sm" />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-ga-text-muted">
                    No upcoming bookings
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights + Payments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="ga-card">
          <h2 className="text-lg md:text-xl font-bold text-ga-text-primary mb-4">Insights &amp; trends</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-ga-sm bg-ga-primary-soft/30 border border-ga-border-subtle">
              <span className="text-sm text-ga-text-muted">Most popular service</span>
              <span className="text-sm font-medium text-ga-text-primary">{dynamicInsights.mostPopularService}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-ga-sm bg-ga-primary-soft/30 border border-ga-border-subtle">
              <span className="text-sm text-ga-text-muted">Peak booking hours</span>
              <span className="text-sm font-medium text-ga-text-primary">{dynamicInsights.peakBookingHours}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-ga-sm bg-ga-primary-soft/30 border border-ga-border-subtle">
              <span className="text-sm text-ga-text-muted">Most booked slot</span>
              <span className="text-sm font-medium text-ga-text-primary">{dynamicInsights.mostBookedSlot}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-ga-sm bg-ga-primary-soft/30 border border-ga-border-subtle">
              <span className="text-sm text-ga-text-muted">Cancellations today</span>
              <span className="text-sm font-medium text-ga-danger">{dynamicInsights.cancellationsToday}</span>
            </div>
          </div>
        </div>

        <div className="ga-card">
          <h2 className="text-lg md:text-xl font-bold text-ga-text-primary mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5" aria-hidden /> Payments overview
          </h2>
          <div className="space-y-2 text-sm">
            {summaryLoading ? (
              <p className="text-ga-text-subtle">Loading payment summary…</p>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-ga-text-muted">Collected today</span>
                  <span className="font-medium text-ga-text-primary">{formatInr(paymentsSummary?.collectedToday ?? 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ga-text-muted">Pending</span>
                  <span className="font-medium text-ga-text-primary">{formatInr(paymentsSummary?.pendingAmount ?? 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ga-text-muted">Pending bookings</span>
                  <span className="font-medium text-ga-text-primary">{paymentsSummary?.pendingCount ?? 0}</span>
                </div>
              </>
            )}
          </div>
          <Link to="/dashboard/payments" className="mt-4 inline-block text-sm text-ga-primary hover:underline">
            View all payments →
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-8 flex flex-wrap items-center justify-between gap-2 py-4 border-t border-ga-border-subtle text-sm text-ga-text-subtle">
        <span>
          {lastSyncedAt ? `Last synced: ${Math.max(0, Math.floor((Date.now() - lastSyncedAt.getTime()) / 60000))} min ago` : 'Syncing…'}
        </span>
        <span>Groundaale v1.0</span>
        <Link to="/dashboard/help-center" className="text-ga-primary hover:underline">Need help?</Link>
      </footer>
    </>
  );
}
